import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Form, Button } from 'react-bootstrap';
import { toastr } from 'react-redux-toastr';

import Loader from '../../../../components/Common/Loader/Loader';
import Link from '../../../../components/Link';
import getByIdQuery from './getSeoLandingPageById.graphql';
import updateMutation from './updateSeoLandingPage.graphql';

const EditSeoPage = ({ id }) => {
  const { loading, data } = useQuery(getByIdQuery, { variables: { id }, fetchPolicy: 'network-only' });
  const [save, { loading: saving }] = useMutation(updateMutation);
  const [form, setForm] = useState(null);

  const page = data?.getSeoLandingPageById;

  useEffect(() => {
    if (page && page.status === 200 && !form) {
      setForm({
        title: page.title || '', h1: page.h1 || '', metaDescription: page.metaDescription || '',
        metaKeywords: page.metaKeywords || '', introHtml: page.introHtml || '',
        pageStatus: page.pageStatus || 'draft', isAuto: !!page.isAuto,
      });
    }
  }, [page]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const onSave = async () => {
    try {
      const res = await save({ variables: { id, ...form } });
      const out = res?.data?.updateSeoLandingPage;
      if (out?.status === 200) toastr.success('Saved', 'SEO page updated.');
      else toastr.error('Error', out?.errorMessage || 'Could not save.');
    } catch (e) {
      toastr.error('Error', e.message || 'Could not save.');
    }
  };

  const label = { fontWeight: 600 };
  const help = { color: '#9AA0A6' };

  return (
    <div style={{ padding: 24, maxWidth: 820 }}>
      <Link to="/siteadmin/seo" style={{ color: '#E8232B' }}>&larr; Back to SEO pages</Link>
      <h1 className="siteAdminPrimaryHeading" style={{ marginTop: 8 }}>Edit SEO Page</h1>

      <Loader type="page" show={loading}>
        {!loading && page && page.status !== 200 && (
          <div style={{ color: '#c0392b' }}>{page.errorMessage || 'Page not found.'}</div>
        )}
        {!loading && form && (
          <>
            <p style={{ color: '#5f6368' }}>
              <b>{page.serviceName}</b> in <b>{page.cityName}, {page.stateCode}</b> &nbsp;·&nbsp;
              <Link to={`${page.path}?preview=1`} style={{ color: '#1a73e8' }}>Preview the page</Link>
            </p>

            <Form.Group style={{ marginBottom: 16 }}>
              <Form.Label style={label}>Page title (SEO &lt;title&gt;)</Form.Label>
              <Form.Control type="text" value={form.title} onChange={e => set('title', e.target.value)} />
              <Form.Text style={help}>Shown in Google results. Keep it ~50–60 characters, include the service + city.</Form.Text>
            </Form.Group>

            <Form.Group style={{ marginBottom: 16 }}>
              <Form.Label style={label}>H1 heading</Form.Label>
              <Form.Control type="text" value={form.h1} onChange={e => set('h1', e.target.value)} />
              <Form.Text style={help}>The big heading at the top of the page.</Form.Text>
            </Form.Group>

            <Form.Group style={{ marginBottom: 16 }}>
              <Form.Label style={label}>Meta description</Form.Label>
              <Form.Control as="textarea" rows={2} value={form.metaDescription} onChange={e => set('metaDescription', e.target.value)} />
              <Form.Text style={help}>The grey text under the title in Google. ~150–160 characters.</Form.Text>
            </Form.Group>

            <Form.Group style={{ marginBottom: 16 }}>
              <Form.Label style={label}>Meta keywords</Form.Label>
              <Form.Control type="text" value={form.metaKeywords} onChange={e => set('metaKeywords', e.target.value)} />
            </Form.Group>

            <Form.Group style={{ marginBottom: 16 }}>
              <Form.Label style={label}>Intro content (HTML)</Form.Label>
              <Form.Control as="textarea" rows={6} value={form.introHtml} onChange={e => set('introHtml', e.target.value)} />
              <Form.Text style={help}>The marketing copy on the page. Basic HTML allowed.</Form.Text>
            </Form.Group>

            <Form.Group style={{ marginBottom: 16 }}>
              <Form.Label style={label}>Status</Form.Label>
              <Form.Control as="select" value={form.pageStatus} onChange={e => set('pageStatus', e.target.value)} style={{ maxWidth: 220 }}>
                <option value="draft">Draft (hidden)</option>
                <option value="published">Published (live)</option>
              </Form.Control>
            </Form.Group>

            <Form.Group style={{ marginBottom: 20 }}>
              <Form.Check
                type="checkbox" id="isAuto" checked={!!form.isAuto}
                onChange={e => set('isAuto', e.target.checked)}
                label="Auto-managed (let the generator refresh this page)"
              />
              <Form.Text style={help}>
                Leave <b>unchecked</b> after editing so the nightly generator won’t overwrite your changes.
                Check it to hand the page back to automatic content.
              </Form.Text>
            </Form.Group>

            <Button variant="primary" disabled={saving} onClick={onSave}>{saving ? 'Saving…' : 'Save'}</Button>
          </>
        )}
      </Loader>
    </div>
  );
};

export default EditSeoPage;
